import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, addDoc, onSnapshot, query, orderBy, where,
  deleteDoc, doc, updateDoc, Timestamp 
} from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import Swal from 'sweetalert2';

function Staffmgmt() {
  // Existing Form States
  const [staffName, setStaffName] = useState("");
  const [mobile, setMobile] = useState("");
  const [role, setRole] = useState("Attendant"); 
  const [address, setAddress] = useState("");
  const [status, setStatus] = useState("Active");

  // NEW: Personal & Bank Details States
  const [aadharNo, setAadharNo] = useState("");
  const [bankName, setBankName] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [branch, setBranch] = useState("");
  const [ifsc, setIfsc] = useState("");

  // Auth & System States
  const [userId, setUserId] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);

  // Edit States
  const [editId, setEditId] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  const Toast = Swal.mixin({
    toast: true,
    position: 'top-end',
    showConfirmButton: false,
    timer: 3000,
  });

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (user) { setUserId(user.uid); } 
      else { setUserId(null); setStaffList([]); }
      setAuthLoading(false);
    });
    return () => unsubscribeAuth();
  }, []);

  useEffect(() => {
    if (!userId) return;
    const q = query(collection(db, "staff"), where("userId", "==", userId), orderBy("created", "desc"));
    const unsubscribeData = onSnapshot(q, (snapshot) => {
      setStaffList(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    return () => unsubscribeData();
  }, [userId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!userId) return Toast.fire({ icon: 'error', title: 'User not authenticated' });
    if (!staffName || !mobile) return Toast.fire({ icon: 'warning', title: 'Name and Mobile are required' });

    setLoading(true);
    try {
      const staffData = {
        name: staffName.toUpperCase(),
        mobile: mobile,
        role: role,
        address: address,
        status: status,
        // New Fields
        aadharNo: aadharNo,
        bankName: bankName,
        accountNo: accountNo,
        branch: branch,
        ifsc: ifsc.toUpperCase(),
        updatedAt: Timestamp.now(),
        userId: userId 
      };

      if (isEditing) {
        await updateDoc(doc(db, "staff", editId), staffData);
        Toast.fire({ icon: 'success', title: 'Staff Updated' });
      } else {
        await addDoc(collection(db, "staff"), { ...staffData, created: Timestamp.now() });
        Toast.fire({ icon: 'success', title: 'Staff Registered' });
      }
      resetForm();
    } catch (error) {
      Toast.fire({ icon: 'error', title: error.message });
    } finally { setLoading(false); }
  };

  const handleEdit = (member) => {
    setIsEditing(true);
    setEditId(member.id);
    setStaffName(member.name);
    setMobile(member.mobile);
    setRole(member.role);
    setAddress(member.address);
    setStatus(member.status);
    // New Fields
    setAadharNo(member.aadharNo || "");
    setBankName(member.bankName || "");
    setAccountNo(member.accountNo || "");
    setBranch(member.branch || "");
    setIfsc(member.ifsc || "");
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const resetForm = () => {
    setIsEditing(false);
    setEditId(null);
    setStaffName("");
    setMobile("");
    setRole("Attendant");
    setAddress("");
    setStatus("Active");
    setAadharNo("");
    setBankName("");
    setAccountNo("");
    setBranch("");
    setIfsc("");
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Remove Staff?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
    });
    if (result.isConfirmed) {
      await deleteDoc(doc(db, "staff", id));
      Toast.fire({ icon: 'success', title: 'Staff Removed' });
    }
  };

  if (authLoading) return <div className="content-wrapper d-flex justify-content-center align-items-center" style={{ minHeight: '80vh' }}><div className="spinner-border text-info"></div></div>;

  return (
    <div className="content-wrapper">
      <section className="content-header text-navy p-4">
        <h1><i className="fas fa-users-cog mr-2"></i>Staff Management</h1>
      </section>

      <section className="content px-4">
        <div className="row">
          {/* FORM SECTION */}
          <div className="col-md-5">
            <div className={`card ${isEditing ? 'card-warning' : 'card-navy shadow-lg border-0'}`}>
              <div className="card-header" style={{backgroundColor: isEditing ? '' : '#0f172a', color: '#fff'}}>
                <h3 className="card-title">{isEditing ? 'Edit Staff Details' : 'Register New Staff'}</h3>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="card-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                  <h6 className="text-primary border-bottom pb-2 mb-3">Basic Information</h6>
                  <div className="row">
                    <div className="col-md-12">
                      <div className="form-group">
                        <label>Full Name</label>
                        <input type="text" className="form-control" value={staffName} onChange={(e) => setStaffName(e.target.value)} required />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Mobile Number</label>
                        <input type="number" className="form-control" value={mobile} onChange={(e) => setMobile(e.target.value)} required />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Aadhaar Number</label>
                        <input type="number" className="form-control" value={aadharNo} onChange={(e) => setAadharNo(e.target.value)} placeholder="12 Digit No" />
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Role</label>
                    <select className="form-control" value={role} onChange={(e) => setRole(e.target.value)}>
                      <option value="Attendant">Nozzle Man</option>
                      <option value="Manager">Manager</option>
                      <option value="Accountant">Accountant</option>
                    </select>
                  </div>

                  <h6 className="text-primary border-bottom pb-2 mt-4 mb-3">Bank Details</h6>
                  <div className="form-group">
                    <label>Bank Name</label>
                    <input type="text" className="form-control" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="e.g. State Bank of India" />
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Account Number</label>
                        <input type="text" className="form-control" value={accountNo} onChange={(e) => setAccountNo(e.target.value)} />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>IFSC Code</label>
                        <input type="text" className="form-control" value={ifsc} onChange={(e) => setIfsc(e.target.value)} />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Branch Name</label>
                    <input type="text" className="form-control" value={branch} onChange={(e) => setBranch(e.target.value)} />
                  </div>

                  <h6 className="text-primary border-bottom pb-2 mt-4 mb-3">Other Details</h6>
                  <div className="form-group">
                    <label>Address</label>
                    <textarea className="form-control" rows="2" value={address} onChange={(e) => setAddress(e.target.value)}></textarea>
                  </div>
                  <div className="form-group">
                    <label>Employment Status</label>
                    <select className="form-control" value={status} onChange={(e) => setStatus(e.target.value)}>
                      <option value="Active">Active</option>
                      <option value="Inactive">Resigned/Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="card-footer bg-white border-0">
                  <button type="submit" disabled={loading} className={`btn btn-block shadow-sm ${isEditing ? 'btn-warning' : 'btn-primary'}`}>
                    {loading ? 'Processing...' : isEditing ? 'UPDATE STAFF' : 'REGISTER STAFF'}
                  </button>
                  {isEditing && <button type="button" onClick={resetForm} className="btn btn-default btn-block mt-2">Cancel</button>}
                </div>
              </form>
            </div>
          </div>

          {/* LIST SECTION */}
          <div className="col-md-7">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white">
                <h3 className="card-title font-weight-bold">Staff Records</h3>
              </div>
              <div className="card-body p-0">
                <table className="table m-0 table-hover">
                  <thead>
                    <tr>
                      <th>Staff Info</th>
                      <th>Bank & Aadhaar</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffList.map((member) => (
                      <tr key={member.id}>
                        <td>
                          <div className="font-weight-bold">{member.name}</div>
                          <small className="text-muted"><i className="fas fa-phone mr-1"></i>{member.mobile}</small><br/>
                          <span className="badge badge-info">{member.role}</span>
                        </td>
                        <td>
                          <small><b>UID:</b> {member.aadharNo || 'N/A'}</small><br/>
                          <small><b>Bank:</b> {member.bankName || 'N/A'}</small><br/>
                          <small><b>Acc:</b> {member.accountNo || 'N/A'}</small>
                        </td>
                        <td>
                          <span className={`badge ${member.status === 'Active' ? 'bg-success' : 'bg-secondary'}`}>
                            {member.status}
                          </span>
                        </td>
                        <td>
                          <button onClick={() => handleEdit(member)} className="btn btn-sm text-primary"><i className="fas fa-edit"></i></button>
                          <button onClick={() => handleDelete(member.id)} className="btn btn-sm text-danger"><i className="fas fa-trash"></i></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Staffmgmt;